"use client";

import { ServicePageLayout, type ServicePageData } from "@/components/ServicePageLayout";
import { useTranslation } from "@/lib/i18n";
import {
  IP_TRANSIT_SLA_PERCENT,
  BGP_PREFIX_COUNT_LABEL,
  SOFIA_CONNECT_ASN,
  HERO_SOFIA_FRANKFURT_LATENCY_MS,
  HERO_SOFIA_ISTANBUL_LATENCY_MS,
  formatLatencyApproxNoSpace,
} from "@/lib/network-metrics";
import {
  leadTimeFor,
  formatLeadTimeDays,
} from "@/data/provisioning-lead-times";
import {
  IP_TRANSIT_MIN_LABEL,
  IP_TRANSIT_MAX_LABEL,
  IP_TRANSIT_CAPACITY_RANGE_LABEL,
} from "@/lib/pricing";

const ucIcon = (d: string) => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const heroIcon = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
            <path strokeLinecap="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
          </svg>
);

const UC_ICONS = ["M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3", "M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15", "M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z"].map((d) => ucIcon(d));

const COMMON = {
  accentColor: "#0066ff",
  icon: heroIcon,
  ctaText: "",
  ctaService: "ip-transit",
  bilingual: true,
} as const;

const LAT_FRA = formatLatencyApproxNoSpace(HERO_SOFIA_FRANKFURT_LATENCY_MS);
const LAT_IST = formatLatencyApproxNoSpace(HERO_SOFIA_ISTANBUL_LATENCY_MS);

function dataEn(): ServicePageData {
  return {
    ...COMMON,
    title: "IP Transit",
    subtitle: "Connectivity Product",
    heroDescription:
      `Premium global routing delivered from Sofia and Frankfurt. Full-table BGP with ${BGP_PREFIX_COUNT_LABEL} prefixes, optimised paths to every major destination and a ${IP_TRANSIT_SLA_PERCENT} SLA. Port speeds from ${IP_TRANSIT_MIN_LABEL} to ${IP_TRANSIT_MAX_LABEL} with flexible commit and burst billing.`,
    specs: [
      { label: "Capacity", value: IP_TRANSIT_CAPACITY_RANGE_LABEL },
      { label: "BGP Prefixes", value: BGP_PREFIX_COUNT_LABEL },
      { label: "SLA Uptime", value: IP_TRANSIT_SLA_PERCENT },
      { label: "Delivery PoPs", value: "Sofia & Frankfurt" },
      { label: "AS Number", value: String(SOFIA_CONNECT_ASN) },
      { label: "Latency to Frankfurt", value: LAT_FRA },
      { label: "Latency to Istanbul", value: LAT_IST },
      { label: "Provisioning", value: formatLeadTimeDays(leadTimeFor("ip-transit"), "en") },
    ],
    features: [
      {
        title: "Full-Table BGP",
        description:
          `Receive the complete global routing table with ${BGP_PREFIX_COUNT_LABEL} prefixes. Full control over your traffic engineering with BGP communities and prefix filtering.`,
      },
      {
        title: "Multi-Homed Architecture",
        description:
          "Redundant upstream connectivity through multiple Tier-1 carriers ensures zero single points of failure and optimal path selection.",
      },
      {
        title: "DDoS Mitigation Included",
        description:
          "Built-in volumetric DDoS protection at no extra cost. Automatic detection and scrubbing with sub-second response times.",
      },
      {
        title: "Flexible Billing Models",
        description:
          "Choose from commit-based, 95th percentile, or burstable billing. Scale capacity up or down with 30-day notice.",
      },
      {
        title: "24/7 NOC Support",
        description:
          "Round-the-clock network operations center with 15-minute SLA response for P1 incidents. Direct access to senior engineers.",
      },
      {
        title: "Real-Time Traffic Analytics",
        description:
          "Access detailed traffic graphs, BGP session monitoring, and per-prefix analytics through the customer portal.",
      },
    ],
    useCases: [
      {
        title: "ISP & Carrier Peering",
        description:
          "Upstream transit for regional ISPs needing reliable, low-latency access to the global routing table with full BGP support.",
        icon: UC_ICONS[0],
      },
      {
        title: "Enterprise Multi-Site",
        description:
          "Global internet access for enterprises with distributed offices, data centers, and cloud workloads requiring consistent performance.",
        icon: UC_ICONS[1],
      },
      {
        title: "Cloud & CDN On-Ramp",
        description:
          "Direct, low-latency connectivity to major cloud platforms and CDN networks through Sofia Connect's dense peering ecosystem.",
        icon: UC_ICONS[2],
      },
    ],
  };
}

/* Bulgarian copy translates the English page one to one. No figure is
 * added or changed; the numbers read from the same constants. */
function dataBg(): ServicePageData {
  return {
    ...COMMON,
    title: "IP Transit",
    subtitle: "Услуга за свързаност",
    heroDescription:
      `Глобално маршрутизиране от висок клас с доставка от София и Франкфурт. Пълна BGP таблица с ${BGP_PREFIX_COUNT_LABEL} префикса, оптимизирани пътища до всички основни направления и SLA ${IP_TRANSIT_SLA_PERCENT}. Портове от ${IP_TRANSIT_MIN_LABEL} до ${IP_TRANSIT_MAX_LABEL} с гъвкаво таксуване по ангажиран капацитет и пикове.`,
    specs: [
      { label: "Капацитет", value: IP_TRANSIT_CAPACITY_RANGE_LABEL },
      { label: "BGP префикси", value: BGP_PREFIX_COUNT_LABEL },
      { label: "SLA за достъпност", value: IP_TRANSIT_SLA_PERCENT },
      { label: "Точки на доставка", value: "София и Франкфурт" },
      { label: "Номер на AS", value: String(SOFIA_CONNECT_ASN) },
      { label: "Латентност до Франкфурт", value: LAT_FRA },
      { label: "Латентност до Истанбул", value: LAT_IST },
      { label: "Срок за доставка", value: formatLeadTimeDays(leadTimeFor("ip-transit"), "bg") },
    ],
    features: [
      {
        title: "Пълна BGP таблица",
        description:
          `Получавате пълната глобална таблица за маршрутизация с ${BGP_PREFIX_COUNT_LABEL} префикса. Пълен контрол върху управлението на трафика чрез BGP communities и филтриране на префикси.`,
      },
      {
        title: "Свързаност през няколко доставчика",
        description:
          "Резервирана свързаност нагоре през няколко Tier-1 оператора, без единична точка на отказ и с избор на оптимален път.",
      },
      {
        title: "Защита от DDoS",
        description:
          "Вградена защита от обемни DDoS атаки без допълнително заплащане. Автоматично откриване и почистване на трафика с реакция под секунда.",
      },
      {
        title: "Гъвкаво таксуване",
        description:
          "Изберете таксуване по ангажиран капацитет, по 95-ия перцентил или с възможност за пикове. Капацитетът се увеличава или намалява с 30-дневно предизвестие.",
      },
      {
        title: "NOC 24/7",
        description:
          "Денонощен център за мрежови операции с реакция по SLA до 15 минути при инциденти P1. Пряк достъп до старши инженери.",
      },
      {
        title: "Анализ на трафика в реално време",
        description:
          "Подробни графики на трафика, наблюдение на BGP сесиите и анализ по префикси през клиентския портал.",
      },
    ],
    useCases: [
      {
        title: "Интернет доставчици и оператори",
        description:
          "Транзит нагоре за регионални интернет доставчици, които се нуждаят от надежден достъп с ниска латентност до глобалната таблица за маршрутизация с пълна поддръжка на BGP.",
        icon: UC_ICONS[0],
      },
      {
        title: "Компании с много обекти",
        description:
          "Глобален достъп до интернет за компании с офиси, центрове за данни и облачни натоварвания на различни места, които изискват постоянна производителност.",
        icon: UC_ICONS[1],
      },
      {
        title: "Достъп до облаци и CDN",
        description:
          "Пряка свързаност с ниска латентност към големите облачни платформи и CDN мрежи през широката пиъринг мрежа на Sofia Connect.",
        icon: UC_ICONS[2],
      },
    ],
  };
}

export function IpTransitClient() {
  const { language } = useTranslation();
  return <ServicePageLayout data={language === "bg" ? dataBg() : dataEn()} />;
}
